import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  nome_cognome: "",
  telefono: "",
  email: "",
  tipo_servizio: "",
  messaggio: "",
};

const FIELD_CLASS =
  "w-full bg-[#0B0C0E] border border-white/10 focus:border-[#D97736]/60 focus:ring-2 focus:ring-[#D97736]/20 outline-none rounded-xl px-4 py-3.5 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] transition-[border-color,box-shadow] duration-300";

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/preventivi`, form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Richiesta inviata con successo", {
        description: "Ti ricontatteremo entro 24 ore lavorative.",
      });
    } catch (err) {
      toast.error("Errore nell'invio della richiesta", {
        description: "Riprova tra poco oppure chiamaci al +39 351 747 9343.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="preventivo" className="py-24 sm:py-32 relative overflow-hidden" data-testid="quote-section">
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(217,119,54,0.08),transparent_60%)]"
        aria-hidden="true"
      />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D97736] mb-4">
            Preventivo gratuito e senza impegno
          </p>
          <h2 className="font-syne font-bold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl">
            Raccontaci il tuo tetto.
          </h2>
          <p className="mt-4 text-base text-[#9CA3AF] max-w-xl mx-auto">
            Compila il modulo oppure chiamaci direttamente:{" "}
            <a href="tel:+393517479343" className="text-[#D97736] hover:text-[#E88D52] font-mono text-sm">
              +39 351 747 9343
            </a>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="bg-[#13151A] border border-white/10 rounded-3xl p-6 sm:p-12 shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
        >
          {sent ? (
            <div data-testid="quote-form-success-toast" className="text-center py-10">
              <CheckCircle2 size={52} className="mx-auto text-[#D97736] mb-5" strokeWidth={1.5} />
              <h3 className="font-syne font-bold text-2xl mb-3">Richiesta ricevuta!</h3>
              <p className="text-[#9CA3AF] max-w-md mx-auto mb-8">
                Grazie per averci contattato. Un nostro tecnico ti richiamerà entro 24 ore lavorative per
                fissare il sopralluogo gratuito.
              </p>
              <button
                data-testid="quote-form-new-request-button"
                onClick={() => setSent(false)}
                className="font-outfit text-sm font-medium text-[#D97736] hover:text-[#E88D52] transition-colors"
              >
                Invia un'altra richiesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="quote-form">
              <div>
                <label htmlFor="qf-nome" className="block font-outfit text-xs uppercase tracking-[0.15em] text-[#9CA3AF] mb-2">
                  Nome e Cognome *
                </label>
                <input
                  id="qf-nome"
                  data-testid="quote-form-name-input"
                  type="text"
                  required
                  minLength={2}
                  value={form.nome_cognome}
                  onChange={set("nome_cognome")}
                  placeholder="es. Marco Rossi"
                  className={FIELD_CLASS}
                />
              </div>
              <div>
                <label htmlFor="qf-tel" className="block font-outfit text-xs uppercase tracking-[0.15em] text-[#9CA3AF] mb-2">
                  Numero di Telefono *
                </label>
                <input
                  id="qf-tel"
                  data-testid="quote-form-phone-input"
                  type="tel"
                  required
                  minLength={6}
                  value={form.telefono}
                  onChange={set("telefono")}
                  placeholder="+39 333 1234567"
                  className={FIELD_CLASS}
                />
              </div>
              <div>
                <label htmlFor="qf-email" className="block font-outfit text-xs uppercase tracking-[0.15em] text-[#9CA3AF] mb-2">
                  Email *
                </label>
                <input
                  id="qf-email"
                  data-testid="quote-form-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="marco.rossi@email.it"
                  className={FIELD_CLASS}
                />
              </div>
              <div>
                <label htmlFor="qf-servizio" className="block font-outfit text-xs uppercase tracking-[0.15em] text-[#9CA3AF] mb-2">
                  Intervento Richiesto *
                </label>
                <select
                  id="qf-servizio"
                  data-testid="quote-form-service-select"
                  required
                  value={form.tipo_servizio}
                  onChange={set("tipo_servizio")}
                  className={FIELD_CLASS}
                >
                  <option value="" disabled>
                    Seleziona un intervento
                  </option>
                  <option>Impermeabilizzazione in Guaina</option>
                  <option>Copertura in Tegole Tradizionali</option>
                  <option>Pannelli Finto Coppo Coibentati</option>
                  <option>Ristrutturazione Completa Tetto</option>
                  <option>Altro / Consulenza Generale</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="qf-msg" className="block font-outfit text-xs uppercase tracking-[0.15em] text-[#9CA3AF] mb-2">
                  Dettagli dell'intervento / Note
                </label>
                <textarea
                  id="qf-msg"
                  data-testid="quote-form-message-input"
                  rows={4}
                  value={form.messaggio}
                  onChange={set("messaggio")}
                  placeholder="Descrivi brevemente lo stato attuale del tetto o la superficie in mq..."
                  className={`${FIELD_CLASS} resize-none`}
                />
              </div>
              <div className="sm:col-span-2 flex flex-col sm:flex-row items-center gap-4 pt-2">
                <button
                  type="submit"
                  data-testid="quote-form-submit-button"
                  disabled={sending}
                  className="group w-full sm:w-auto flex items-center justify-center gap-3 bg-[#D97736] hover:bg-[#E88D52] disabled:opacity-60 text-[#0B0C0E] font-outfit font-semibold rounded-full px-8 py-4 transition-colors duration-300"
                >
                  {sending ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                  )}
                  {sending ? "Invio in corso..." : "Invia Richiesta di Preventivo Gratuito"}
                </button>
                <a
                  href="tel:+393517479343"
                  className="flex items-center gap-2 font-mono text-xs text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                >
                  <Phone size={13} className="text-[#D97736]" />
                  Preferisci parlare? +39 351 747 9343
                </a>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
